import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { ImagesToImageMap } from '@/lib/core/utils';
import { ScreenHead } from '@/components/shared/screen/head';
import { BackgroundVideo } from '@/components/shared/video/background';
import { Button } from '@/components/ui/button';
import {
  SectionItemsElementModelContentType,
  ShopIntroContentResponseModel,
  ShopIntroPropertiesModel,
  type SectionItemPropertiesModel,
  type SectionItemsPropertiesModel,
} from '@/services/platform/api/model';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ShopIntroScreenProps {
  /** CMS page content — drives hero image, title, logo and subtitle. */
  content?: ShopIntroContentResponseModel;
}

// ── CMS-driven section tiles ─────────────────────────────────────────────────

interface SectionTile {
  key: string;
  label: string;
  href: string;
}

/** Groups CMS `sectionItem`s into rows of 2 for the tile grid. */
function chunkPairs<T>(items: T[]): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += 2) rows.push(items.slice(i, i + 2));
  return rows;
}

/** Reads `properties.components` → `sectionItems` block → `sectionList.items` (`sectionItem`s). */
function getSectionTiles(components?: ShopIntroPropertiesModel['components']): SectionTile[] {
  const sectionItemsBlock = components?.items?.find(
    item => item.content?.contentType === SectionItemsElementModelContentType.sectionItems
  );
  const sectionItemsProps = (
    sectionItemsBlock?.content as unknown as { properties?: SectionItemsPropertiesModel } | undefined
  )?.properties;

  const items = sectionItemsProps?.sectionList?.items ?? [];

  return items.reduce<SectionTile[]>((tiles, item, index) => {
    const itemProps = (item.content as unknown as { properties?: SectionItemPropertiesModel } | undefined)
      ?.properties;
    const link = itemProps?.link?.[0];
    const href = link?.route?.path ?? link?.url ?? undefined;
    if (!href) return tiles;
    tiles.push({ key: `${index}-${href}`, label: link?.title ?? '', href });
    return tiles;
  }, []);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ShopIntroScreen({ content }: ShopIntroScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const handleBack = () => navigate(-1);

  /**
   * TODO: Replace with CMS-delivered properties once ShopIntroPropertiesModel
   * fields are wired up in the backend:
   *  - pageHero   → hero background image (ImageWithCrops[])
   *  - shopAt     → "SHOP AT" label text override
   *  - shopLogo   → Fashion Clinic logo image (ImageWithCrops[])
   *  - subtitle   → headline copy under the logo
   */
  const setMainClassName = useUIStore(state => state.setMainClassName);
  const setNavStyleOverride = useUIStore(state => state.setNavStyleOverride);

  useEffect(() => {
    setMainClassName('pb-0');
    setNavStyleOverride('default');
    return () => {
      setMainClassName('');
      setNavStyleOverride(null);
    };
  }, [setMainClassName, setNavStyleOverride]);

  const props = content?.properties as ShopIntroPropertiesModel | undefined;
  const bgImage = ImagesToImageMap(props?.pageHero);
  const shopAtLabel = props?.shopAt ?? t('App.Shop.ShopAt');
  const subtitleText = props?.subtitle ?? t('App.Shop.SubTitle');

  /**
   * TODO: Replace with CMS-delivered Fashion Clinic logo image (props?.shopLogo)
   * once the backend delivers it. Currently falls back to text label.
   */
  const logoImage = ImagesToImageMap(props?.shopLogo);
  const sectionRows = chunkPairs(getSectionTiles(props?.components));

  const isVideo = bgImage?.type === 'umbracoMediaVideo';

  return (
    <div
      className="relative min-h-svh w-full overflow-hidden"
      style={
        bgImage?.src && !isVideo
          ? { backgroundImage: `url(${bgImage.src})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : { background: 'linear-gradient(to bottom, #2a1a0e, var(--jncquoi-neutral-1000))' }
      }
    >
      {/*
       * Video background — used when CMS pageHero is an umbracoMediaVideo.
       * TODO: if the backend adds a dedicated video field to ShopIntroPropertiesModel,
       * read from props.pageHeroVideo instead of reusing pageHero.
       */}
      {isVideo && <BackgroundVideo src={bgImage?.src} />}

      {/* Subtle multiply-blend overlay from Figma */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.10) 100%)' }}
      />

      {/*
       * Header — gradient fades from opaque black at top to transparent below,
       * ensuring legibility of the back arrow and title over the hero image.
       */}
      <div className="pt-safe-area absolute top-0 right-0 left-0 z-10 bg-linear-to-b from-black/80 to-transparent pb-4">
        <ScreenHead
          title={t('App.Shop.FashionClinic')}
          onBack={handleBack}
          ariaLabel={t('App.Labels.GoBack')}
          variant="overlay"
          className="pt-0"
        />
      </div>

      {/* Main content pinned to the bottom of the viewport */}
      <div className="absolute right-0 bottom-0 left-0 px-4 pb-[calc(((env(safe-area-inset-bottom)+1.5rem)*2)+48px)]">
        {/* Shop At + Logo + Subtitle */}
        <div className="mb-10 flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            {/* "SHOP AT" label */}
            <span className="font-pp-neue-montreal text-[12px] font-normal tracking-[0.5px] text-white uppercase">
              {shopAtLabel}
            </span>

            {/* Fashion Clinic logo — TODO: swap <span> for <img> when CMS delivers shopLogo */}
            {logoImage?.src ? (
              <img
                src={logoImage.src}
                alt={t('App.Shop.FashionClinic')}
                className="h-6 object-contain object-left"
              />
            ) : (
              <span className="font-pp-neue-montreal text-[20px] font-semibold tracking-wider text-white uppercase">
                {t('App.Shop.FashionClinic')}
              </span>
            )}
          </div>

          {/* Subtitle headline */}
          <p className="font-pp-neue-montreal text-[28px] leading-tight font-semibold tracking-[0.5px] text-white uppercase">
            {subtitleText}
          </p>
        </div>

        {/* CMS-driven section CTA grid — tiles come from the shopIntro `sectionItems` block */}
        <div className="flex flex-col gap-2">
          {sectionRows.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-2">
              {row.map(tile => (
                <Button
                  key={tile.key}
                  variant="secondary-outline"
                  palette="white"
                  size="lg"
                  className="flex-1 rounded-[2px]"
                  onClick={() => navigate(tile.href)}
                >
                  {tile.label}
                </Button>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
